import { useState, useRef, useCallback, useEffect, useEffectEvent } from "react";

import { WebSocketServer } from "ws";

import { get_op_by_hex } from './ops_table.js'
import CollapsibleInfoPanel from './CollapsibleInfoPanel';

const cpuHost = import.meta.env.VITE_API_URL || 'http://localhost:8080'
const cpuPath = '/command'
const cpuUrl = cpuHost + cpuPath
const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8080'

function MemTable({title, children}) {
  return (
    <div className="card" style={{ margin: '1em' }}>
      <h2>{title}</h2>
      <table style={{ tableLayout: 'fixed', margin: 'auto', width: '95%' }}>
        {children}
      </table>
    </div>
  );
}

function RegTableHeader({headerMem}) {
  return (
    <thead>
      <tr>
        {
          headerMem.map((r, i) => (
            <th key={i} style={{border: '1px solid white', padding: '.5em', width: '20%', margin: 'auto'}}>
              {r[0].slice(-1).toUpperCase()}
            </th>
          ))
        }
      </tr>
    </thead>
  )
}

function RegTableBody({bodyMem}) {
  return (
    <tbody>
      <tr>
        {
          bodyMem.map((r, i) => (
            <td key={i} style={{border: '1px solid white', padding: '.5em', width: 'auto'}}>
              {r[1].toString(16).padStart(2, '0')}
            </td>
          ))
        }
      </tr>
    </tbody>
  )
}

function RegTableBodyFull({headerMem, bodyMem}) {
  return (
    <div className="grid grid-cols-6 px-4 py-1">
        {
          headerMem.map((r, i) => (
              <div key={i}>
                {r[0].slice(-1).toUpperCase()}
              </div>
            ))
        }
        {
          bodyMem.map((r, i) => (
            <div key={i} className={"border-1 " + (i > 0 ? "border-l-0" : "") + " border-solid"}>
              {r[1].toString(16).padStart((i === 5 ? 4 : 2) , '0')}
            </div>
          ))
        }
      
    </div>
  )
}

function RamTableHeader({headerMem}) {
  return (
    <thead>
      <tr>
        {
          headerMem.map((r, i) => (
            <th key={i} style={{border: '1px solid white'}}>
              {r[0].toString(16).padStart(2, '0')}
            </th>
          ))
        }
      </tr>
    </thead>
  )
}

function RamTableBody({bodyMem, regPC, clickHandler, rowLength}) {
  const rows = [];
  for (let i = 0; i < bodyMem.length; i += rowLength) {
    rows.push(<RamTableRow key={i} rowMem={bodyMem.slice(i, i + rowLength)} regPC={regPC} clickHandler={clickHandler}/>);
  }
  return (
    <tbody>
      {rows}
    </tbody>
  )
}

function RamTableRow({rowMem, regPC, clickHandler}) {
  return (
    <tr>
      {
        rowMem.map((cell, i) => (
          <RamCell key={i} cellMem={cell[1]} regPC={regPC} clickHandler={clickHandler} cellKey={i} />
        ))
      }
    </tr>
  )
}

function RamCell({cellMem, regPC, clickHandler, cellKey}) {
  if (cellMem.address === undefined) {
    return <>ono</>
  }
  let cellOutline = '1px solid #ffffff'
  let cellFontWeight = 'normal'
  if (cellMem.address == regPC) {
    cellOutline = '4px solid #ffffff'
    cellFontWeight = 'bold'
  }
  const cellHexAddress = numberToHexString(cellMem.address)
  const cellHexContents = numberToHexString(cellMem.contents)
  const cellHexMod = numberToHexString((cellMem.contents > 128 ? 0 : 255))
  const cellMnemonic = cellMem.mnemonic
  return (
    <td key={cellKey}>
      <div style={{ outline: cellOutline, 
                    fontWeight: cellFontWeight, 
                    backgroundColor: '#55' + cellHexContents + '55', 
                    lineHeight: '.7em',
                    padding: '.3em',
                    aspectRatio: '1/1'
      }} onClick={async () => clickHandler(cellHexAddress) } >
        <span style={{fontSize: '.5em', color: '#' + cellHexMod + cellHexMod + cellHexMod}}>
          {cellHexAddress}
        </span>
        <br />
        <span style={{fontSize: '.8em', color: '#' + cellHexMod + cellHexMod + cellHexMod}}>
          {cellHexContents}
        </span>
        <br />
        <span style={{fontSize: '.6em', color: '#' + cellHexMod + cellHexMod + cellHexMod}}>
          {cellMnemonic ?? "___"}
        </span>
      </div>
    </td>
  )
}


function RamTableBodyFull({bodyMem, regPC, clickHandler, rowLength}) {
  let rows = [];
  for (let i = 0; i < bodyMem.length; i += rowLength) {
    let rowMem = bodyMem.slice(i, i + rowLength)
    rows.push(rowMem.map((cell, j) => {
          const cellMem = cell[1]
          if (cellMem.address === undefined) {
            return <>ono</>
          }

          const cellMnemonic = cellMem.mnemonic
          const cellHexAddress = numberToHexString(cellMem.address, 4)
          const cellHexContents = numberToHexString(cellMem.contents)
          const cellHexMod = numberToHexString((cellMem.contents > 128 ? 0 : 255))
          let cellTextColor = '#' + cellHexMod + cellHexMod + cellHexMod
          let cellBackgroundColor = '#55' + cellHexContents + '55'
          let cellVarClass = (cellMem.address === regPC ? 'ramCellLit' : 'ramCellUnlit')
          const cellClassName = 'ramCellBase ' + cellVarClass
          
          return (
              <div key={j}
                className={cellClassName}
                style={{'--dynamic-bg-color': cellBackgroundColor, '--dynamic-text-color': cellTextColor}}
                onClick={async () => clickHandler(cellHexAddress) } >
                <div className="text-[.5em]/3">
                  {cellHexAddress}
                </div>
                <div className="text-[.8em]/3">
                  {cellHexContents}
                </div>
                <div className="text-[.5em]/3">
                  {cellMnemonic ?? "___"}
                </div>
              </div>
          )
        }
      )
    )
  }
  return (
    <div className="grid grid-cols-16 text-center w-full">
      {rows}
    </div>
  )
}

function RomMetadataCard({romMetadata}) {
  let rows = []
  
  for (let key in romMetadata) {
    rows.push(
              <div key={key} className='grid grid-cols-3'>
                <div className='col-span-1 px-2 text-right font-bold'>
                  {key}:
                </div>
                <div className='col-span-2 text-left'>
                  { (key === 'id' ? '#' : '') + numberToHexString(romMetadata[key]) }
                </div>
              </div>
    )
  }

  return (
    <div>
      {rows}
    </div>
  )
}

function ControlCheckbox({controlValue, controlLabel, onControlChange}) {
  return (
    <label className="p-1">
      <input
        className="px-1"
        type="checkbox"
        checked={controlValue}
        onChange={e => {
          onControlChange(e.target.checked)
        }}
      />
      <span className="p-1">
        {controlLabel}
      </span>
    </label>
  )
}

export default function MonitorApp({ externalProps = [] }) {
  const [memData, setMemData] = useState( { regs: [['reg_a', 0xde], ['reg_x', 0xad], ['reg_y', 0xbe], ['reg_s', 0xef], ['reg_p', 0xcc], ['reg_pc', 0x00]], 
                                            rams:   [["0", {"address":0x00,"contents":0xde,"mnemonic":"test"}],
                                                     ["1", {"address":0x01,"contents":0xad}],
                                                     ["2", {"address":0x02,"contents":0xbe}], 
                                                     ["3", {"address":0x03,"contents":0xef}]] } )

  const [memDataB, setMemDataB] = useState( { regs: [['reg_a', 0xde], ['reg_x', 0xad], ['reg_y', 0xbe], ['reg_s', 0xef], ['reg_p', 0xcc], ['reg_pc', 0x00]], 
                                            rams:   [["4", {"address":0x04,"contents":0xde,"mnemonic":"test"}],
                                                     ["5", {"address":0x05,"contents":0xad}],
                                                     ["6", {"address":0x06,"contents":0xbe}], 
                                                     ["7", {"address":0x07,"contents":0xef}]] } )

  const [currentInstruction, setCurrentInstruction] = useState({mnemonic: 'INIT', param: ':) (:'})

  const [stepper, setStepper] = useState(0)
  const [stepInterval, setStepInterval] = useState(null)
  const [stepDelay, setStepDelay] = useState(1)
  const [stepDelayInput, setStepDelayInput] = useState(50)
  const [stepMode, setStepMode]= useState(false)

  const [paintMode, setPaintMode]= useState(false)

  const [offsetInput, setOffsetInput] = useState(0x0000)
  const [limitInput, setLimitInput] = useState(0x0200)

  const [offsetBInput, setOffsetBInput] = useState(0x0200)
  const [limitBInput, setLimitBInput] = useState(0x0100)

  const [romMetadata, setRomMetadata] = useState({ rom: 'init' })

  const [displayTest]= useState(false)

  // console.log("props from stimulus: ", externalProps)

  const onStepperTick = useEffectEvent(async () => {
    const tickCommandObj = { op: 'step', doWhatNow: 'run' }
    const tickCommandJSON = JSON.stringify({ instructions: [tickCommandObj]})

    let current_time = new Date()
    const time_str = current_time.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', second:'numeric', fractionalSecondDigits: 3 })

    console.log(`tick stepper ${time_str}`)
    try {
      await submitCommandForm(tickCommandJSON)
      await refreshMemData()
    } catch (err) {
      console.log('tick stepper got err!', err)
    }
  });

  const onMemRefresherTick = useEffectEvent(async () => {
    console.log('tick mem refresher')

    try {
      wsSend({ type: 'sysDataRequest', view: 'a' })
      wsSend({ type: 'sysDataRequest', view: 'b' })
      // await refreshMemData()
    } catch (err) {
      console.log(err)
    }
  });

  const onDisplayTestTick = useEffectEvent(async () => {
    console.log('tick display test')

    const testRamValues = Array.from({length: 512}, () => Math.floor(Math.random() * 0xff));
    const testRams = testRamValues.map( (r, i) => { return [i, { "address": i, "contents": r } ] } )
    const testMemData = { regs: [['reg_a', 0xde], ['reg_x', 0xad], ['reg_y', 0xbe], ['reg_s', 0xef], ['reg_p', 0xcc], ['reg_pc', Math.floor(Math.random() * 0xff)]],
                          rams: testRams }

    let paramSkips = null

    testMemData.rams.map( (r) => {
      if (paramSkips > 0) {   // this is param data, not an op, so skip op labeling
        paramSkips--
      }
      else {
        let currentOp = get_op_by_hex(r[1].contents)
        if (currentOp) {
          r[1].mnemonic = currentOp.mnemonic  
          if (currentOp.len_in_bytes > 1) {
            paramSkips = currentOp.len_in_bytes - 1
          }
        }
      }
    } )

    try {
      setMemData(testMemData)
      const inst = getCurrentInstructionStr(testMemData)
      setCurrentInstruction(inst)
    } catch (err) {
      console.log(err)
    }
  });



  const wsRef = useRef(null);
  const [messages, setMessages] = useState([]);


  const wsSend = useCallback((data) => {
    if (wsRef.current.readyState === 1) {
      wsRef.current?.send(JSON.stringify(data));
    }
  }, []);


  // startup behavior
  useEffect(() => {
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      const messageObj = JSON.parse(event.data);
      // console.log("ws got message: ", messageObj);
      if (messageObj.type === 'sysData') {
        
        const theMemData = { regs: Object.entries(messageObj.regs), rams: Object.entries(messageObj.rams) }

        let paramSkips = 0
        let paramAddressMode = ""
        theMemData.rams.map( (r) => {
          if (paramSkips > 0) {   // this is param data, not an op, so skip op labeling
            paramSkips--
            r[1].mnemonic = paramSymbol(paramAddressMode)
          }
          else {
            let currentOp = get_op_by_hex(r[1].contents)
            if (currentOp) {
              r[1].mnemonic = currentOp.mnemonic  
              if (currentOp.len_in_bytes > 1) {
                paramSkips = currentOp.len_in_bytes - 1
                paramAddressMode = currentOp.address_mode
              }
            }
          } 
        })

        messageObj.view === 'b' ? setMemDataB(theMemData) : setMemData(theMemData)
        // console.log("WS MESSAGE set mem data ", theMemData)

        if (messageObj.view === 'a') {
          const inst = getCurrentInstructionStr(theMemData)
          setCurrentInstruction(inst)
        }
        
      }
      setMessages((prev) => [...prev, event.data]);
    };

    socket.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    socket.onopen = () => {
      console.log("ws connected");
      socket.send(JSON.stringify("ws client socket ready"));
      onStartupHandler()
    };

    document.addEventListener('keydown', onKeyDownHandler)
    //document.addEventListener('keyup', onKeyUpHandler)
    
    return () => {
      socket.close(1000, "component unmounted");
      document.removeEventListener('keydown', onKeyDownHandler)
    };
  }, [])

  useEffect(() => {
    viewUpdateHandler()
    //document.addEventListener('keyup', onKeyUpHandler)
    return () => {
      // nothing needed for cleanup here since it just refreshes
    };
  }, [offsetInput, offsetBInput, limitInput, limitBInput])

  const viewUpdateHandler = useEffectEvent(async (e) => {
    try {
      await refreshMemData()
      console.log('HTTP refreshed through view update handler')
    } catch (err) {
      console.log(err)
    }

    if (wsSend !== undefined) {
      try {
        wsSend({  type: 'sysDataRequest', 
                  offset: offsetInput,  
                  limit: limitInput,
                  view: 'a'
                })
        wsSend({  type: 'sysDataRequest', 
                  offset: offsetBInput,  
                  limit: limitBInput,
                  view: 'b'
                })
        console.log('WS refreshed through view update handler')
      } catch (err) {
        console.log(err)
      }
    }
  })


  const onStartupHandler = useEffectEvent(async (e) => {
    try {
      await refreshMemData()
      console.log('HTTP refreshed on startup')
    } catch (err) {
      console.log(err)
    }

    try {
      wsSend({  type: 'sysDataRequest', 
                offset: offsetInput,  
                limit: limitInput,
                view: 'a'
              })
      wsSend({  type: 'sysDataRequest', 
                offset: offsetBInput,  
                limit: limitBInput,
                view: 'b'
              })
      console.log('WS refreshed on startup')
    } catch (err) {
      console.log(err)
    }



  })

  const onKeyDownHandler = useEffectEvent(async (e) => {
    const keyInt = e.key.charCodeAt(0)
    console.log('keydown of ' + keyInt)
    const commandObj = [
                        { op: 'irpt', param: '#' + numberToHexString(keyInt), doWhatNow: 'run' },
                        ]
    const commandJSON = JSON.stringify({ instructions: commandObj })
    console.log('keydown commandJSON:', commandJSON)
    try {
      await submitCommandForm(commandJSON)
      // await refreshMemData() // disable for now, only needed for debugging, causes issues with refreshing on view input changes
      // console.log('refreshed after keydown')
    } catch (err) {
      console.log(err)
    }  
  })

  const onKeyUpHandler = useEffectEvent(async (e) => {
    const keyInt = e.key.charCodeAt(0)
    console.log('keyup of ' + keyInt)
    const commandObj = [
                        { op: 'push_a', doWhatNow: 'run' },
                        { op: 'load_a', param: '#00', doWhatNow: 'run' },
                        { op: 'store_a', param: '$00ff', doWhatNow: 'run' },
                        { op: 'pull_a', doWhatNow: 'run' }
                      ]
    const commandJSON = JSON.stringify({ instructions: commandObj })
    console.log('keyup commandJSON:', commandJSON)
    try {
      await submitCommandForm(commandJSON);
      await refreshMemData();
      console.log('refreshed after keyup')
    } catch (err) {
      console.log(err)
    }
  })
 

  async function getAPIData(getMemB = false) {
    //console.log("fetching data...")
    const params = new URLSearchParams()
    params.append("offset", getMemB ? offsetBInput : offsetInput)
    params.append("limit", getMemB ? limitBInput : limitInput)
    const the_res = await fetch(cpuUrl + "?" + params)

    const the_json = await the_res.json()
    
    const theMemData = { regs: Object.entries(the_json.regs), rams: Object.entries(the_json.rams) }

    // add op labels
    let paramSkips = 0
    let paramAddressMode = ""
    theMemData.rams.map( (r) => {
      if (paramSkips > 0) {   // this is param data, not an op, so skip op labeling
        paramSkips--
        r[1].mnemonic = paramSymbol(paramAddressMode)
      }
      else {
        let currentOp = get_op_by_hex(r[1].contents)
        if (currentOp) {
          r[1].mnemonic = currentOp.mnemonic  
          if (currentOp.len_in_bytes > 1) {
            paramSkips = currentOp.len_in_bytes - 1
            paramAddressMode = currentOp.address_mode
          }
        }
      } 
    })
    
    //console.log("fetched mem data! :o", theMemData)
    return theMemData
  }

  async function refreshMemData() {
    const currentMemData = await getAPIData()
    setMemData(currentMemData)
    const currentMemDataB = await getAPIData(true)
    setMemDataB(currentMemDataB)
    const inst = getCurrentInstructionStr(currentMemData)
    setCurrentInstruction(inst)
    console.log('refreshed and set mem data state')
  }

  function getCurrentInstructionStr(currentMemData) {
    const currentRegPC = currentMemData.regs[5][1]
    if (!currentMemData.rams[currentRegPC]) {
      return { mnemonic: 'UNKNOWN', param: '????' }
    }
    const currentRam = currentMemData.rams[currentRegPC][1]
    if (currentRam.mnemonic) {
      const currentOpCode = currentRam.contents
      const currentOp = get_op_by_hex(currentOpCode)
      if (!currentOp) {
        return { mnemonic: 'BYTE', param: numberToHexString(currentRam.contents) }
      }

      let currentInstruction = { mnemonic: currentOp.mnemonic}

      let currentParam = null
      let currentParamStr = ''

      if (currentOp.len_in_bytes > 2) {
        currentParam = currentMemData.rams[currentRegPC + 1][1].contents + (currentMemData.rams[currentRegPC + 2][1].contents * 0x0100)
        currentParamStr = numberToHexString(currentParam, 4)
      } else if (currentOp.len_in_bytes > 1) {
        currentParam = currentMemData.rams[currentRegPC + 1][1].contents
        currentParamStr = numberToHexString(currentParam, 2)
      }


      
      if (currentParamStr) {
        switch(currentOp.address_mode) {
          case "zero_page_x_index":
          case "absolute_x_index":
            currentInstruction.param = "$" + currentParamStr + ",x" 
            break
          case "zero_page_y_index":
          case "absolute_y_index":
            currentInstruction.param = "$" + currentParamStr + ",y"
            break
          case "zero_page":
          case "absolute":
          case "absolute_lol":
            currentInstruction.param = "$" + currentParamStr
            break
          case "immediate":
            currentInstruction.param = "#" + currentParamStr
            break
          default:
            currentInstruction.param = "?" + currentParamStr
        } 
      } else {
        currentInstruction.param = "____"
      }

      return currentInstruction
    }
    else {
      return { mnemonic: "BYTE", param: numberToHexString(currentRam.contents) }
    }
  }

  function paramSymbol(addressMode) {
    let symbolStr = ""
    switch(addressMode) {
      case "zero_page_x_index":
      case "absolute_x_index":
        symbolStr = "$$,x"
        break
      case "zero_page_y_index":
      case "absolute_y_index":
        symbolStr = "$$,y"
        break
      case "zero_page":
      case "absolute":
        symbolStr = "$$"
        break
      case "immediate":
        symbolStr = "##"
        break
    } 
    return symbolStr
  }

  const startStepper = (delay) => {
    setStepInterval(setInterval(() => {
      if (stepMode) {
        displayTest ? onDisplayTestTick() : onStepperTick()
      } else {
        onMemRefresherTick()
      }
      setStepper((prev) => prev + 1)
    }, delay));
  }

  const pauseStepper = () => {
    clearInterval(stepInterval);
  }

  const resetStepper = () => {
    setStepper(0);
  }

  async function handleStepperStartClick(e) {
    pauseStepper()
    startStepper(stepDelayInput)
    if (!stepMode) {
      const commandObj = [
                          { op: 'run', doWhatNow: 'run' }
                          ]
      const commandJSON = JSON.stringify({ instructions: commandObj })
      console.log('stepper start command JSON: ', commandJSON)
      try {
        await submitCommandForm(commandJSON);
        await refreshMemData();
        console.log('refreshed after stepper start run command')
      } catch (err) {
        console.log(err)
      }
    }
  }

  async function handleStepperPauseClick(e) {
    // test react comm back to stimulus
    // const event = new CustomEvent("react:trigger", { 
    //  detail: { hello: "woof" } 
    // })
    // window.dispatchEvent(event)

    pauseStepper()
    if (!stepMode) {
      const commandObj = [
                          { op: 'break', doWhatNow: 'run' }
                          ]
      const commandJSON = JSON.stringify({ instructions: commandObj })
      console.log('stepper pause break commandJSON: ', commandJSON)
      try {
        await submitCommandForm(commandJSON);
        await refreshMemData();
        console.log('refreshed after stepper pause break command')
      } catch (err) {
        console.log(err)
      }
    }
  }

  function handleStepDelayInputChange(e) {
    setStepDelayInput(e.target.value)
  }

  function handleOffsetInputChange(e) {
    setOffsetInput(e.target.value)
  }

  function handleLimitInputChange(e) {
    setLimitInput(e.target.value)
  }

  function handleOffsetBInputChange(e) {
    setOffsetBInput(e.target.value)
  }

  function handleLimitBInputChange(e) {
    setLimitBInput(e.target.value)
  }

  async function cellClickHandler(cellHexAddress) { 
    console.log('click on ' + cellHexAddress)
    
    let commandObj = {}
    if (paintMode) {
      commandObj = { op: 'store_a', param: '$' + numberToHexString(cellHexAddress, 4), doWhatNow: 'run' }
    } else {
      commandObj = { op: 'jump', param: '$' + numberToHexString(cellHexAddress, 4), doWhatNow: 'run' }
    }
    const commandJSON = JSON.stringify({instructions: [commandObj]})
    console.log('click commandJSON: ', commandJSON)
    try {
      await submitCommandForm(commandJSON);
      await refreshMemData();
      console.log('refreshed after cell click')
    } catch (err) {
      console.log(err)
    }
  }

  async function handleProgClick(progNumber) { 
    console.log('click on prog button:')
    console.log(progNumber)
    let commandObj = { op: 'prog', param: '#' + numberToHexString(progNumber, 2), doWhatNow: 'run' }
    const commandJSON = JSON.stringify({instructions: [commandObj]})
    console.log('click commandJSON: ', commandJSON)
    try {
      const res = await submitCommandForm(commandJSON)
      setRomMetadata(res)
      //return res.rom_metadata
      await refreshMemData()
      console.log('refreshed after prog click')
      console.log(res)
    } catch (err) {
      console.log(err)
    }
  }

/*
        <MemTable title={ displayTest ? "+++ MONITOR DISPLAY TEST +++" : "rams!" }>
          <RamTableBody bodyMem={memData.rams} regPC={memData.regs[5][1]} clickHandler = {cellClickHandler} rowLength={16} />
        </MemTable>
*/
  return (
    <div className="grid md:grid-cols-3 md:items-start">
      <div className="col-span-3">
        <div className="bg-gray-600 text-white w-full">
          <CollapsibleInfoPanel title="STARTING TIPS">
            <p className="text-3xl">WELCOME TO K9B-64</p>
            <ul className="list-disc text-xl text-left p-2">
              <li>This is the K9B-64! It is an 8-bit microcomputer that lives on the internet.</li>
              <li>Click the <button className="btn btn-panel" onClick={async () => handleProgClick(0xde)}> PROG A </button> button, then the <button className="btn btn-panel" onClick={handleStepperStartClick}>|&gt;</button> button to see the K9B-64 say hello!</li>
              <li>Click the <button className="btn btn-panel" onClick={async () => handleProgClick(0xad)}> PROG B </button> button to play WYRM. To control WYRM use your W, A, S, and D keys!</li>
              <li>You can modify an existing program or write your own. See the <a href="/tutorial" className="underline">Tutorials</a> for more info!</li>
              
            </ul>
            <p className="text-2xl">HAVE A "DOGGONE" GOOD TIME!</p>
          </CollapsibleInfoPanel>
        </div>
      </div>
      <div className="col-span-3 md:col-span-2">
        <div className="text-center text-xl p-1">ram:</div>
        <RamTableBodyFull bodyMem={memData.rams} regPC={memData.regs[5][1]} clickHandler = {cellClickHandler} rowLength={16} />
        <div className="font-bold text-center">
          ^^^ VIEW A ^^^ --- vvv VIEW B vvv
        </div>
        <RamTableBodyFull bodyMem={memDataB.rams} regPC={memDataB.regs[5][1]} clickHandler = {cellClickHandler} rowLength={16} />
      </div>
      <div className="col-span-3 md:col-span-1 md:sticky md:top-16 h-screen overflow-auto text-center">
        <div className="text-xl p-1">registers:</div>
        <RegTableBodyFull headerMem={memData.regs} bodyMem={memData.regs} />
        <div className="text-xl p-1">current instruction:</div>
        <div className="text-3xl font-bold">
          {currentInstruction.mnemonic}
        </div>
        <div className="text-3xl font-bold">
          {currentInstruction.param}
        </div>
        <div>
          { stepMode ? 'step' : 'refresh' } count: {stepper}
          <button className="btn" onClick={resetStepper}>0</button>
        </div>
        <div>
          <label>
            { stepMode ? 'step' : 'refresh' }&nbsp; =&nbsp;
          </label>
          <input
            size="2"
            value={stepDelayInput}
            onChange={handleStepDelayInputChange}
          />  ms
        </div>

        <div className="card p-4">
          <ControlCheckbox controlValue={stepMode} controlLabel="step mode" onControlChange={setStepMode} />
        </div>

        <div className="grid grid-cols-2">
          <div className="col-span-1 text-right">
            <button className="btn" onClick={handleStepperStartClick}>
              |&gt;
            </button>
          </div>
          <div className="col-span-1 text-left">
            <button className="btn" onClick={handleStepperPauseClick}>||</button>
          </div>
          <div className="col-span-1 text-right">
            <button className="btn" onClick={async () => handleProgClick(0xde)}>
              PROG A
            </button>
          </div>
          <div className="col-span-1 text-left">
            <button className="btn" onClick={async () => handleProgClick(0xad)}>
              PROG B
            </button>
          </div>
        </div>

        <div className="card">
          <CommandForm memData={memData} refreshMemData={refreshMemData} setCurrentInstruction={setCurrentInstruction}/>
        </div>
        <div className="card">
          <button className="btn" onClick={ refreshMemData }>
            SYNC MONITOR
          </button>
        </div>
        <div className="p-2">
          <ControlCheckbox controlValue={paintMode} controlLabel="paint mode" onControlChange={setPaintMode} />
        </div>

        <div>
          -----====-----
        </div>
        <div className="card">
          <div className="text-xl py-2">
            CSV-ROM METADATA
          </div>
          <div>
            <RomMetadataCard romMetadata={romMetadata}/>
          </div>
        </div>
        <div>
          -----====-----
        </div>
        <div className="text-xl py-2">
          VIEW CONTROL
        </div>
        <div className="grid grid-cols-2 py-2">
          <div className="text-right px-1">
            startA =&nbsp; 
          </div>
          <div className="text-left px-1">
            <input
              size="4"
              value={offsetInput}
              onChange={handleOffsetInputChange}
            /> bytes
          </div>
          <div className="text-right px-1">
            sizeA =&nbsp; 
          </div>
          <div className="text-left px-1">
            <input
              size="4"
              value={limitInput}
              onChange={handleLimitInputChange}
            /> bytes
          </div>
          <div className="text-right px-1">
            startB =&nbsp; 
          </div>
          <div className="text-left px-1">
            <input
              size="4"
              value={offsetBInput}
              onChange={handleOffsetBInputChange}
            /> bytes
          </div>
          <div className="text-right px-1">
            sizeB =&nbsp; 
          </div>
          <div className="text-left px-1">
            <input
              size="4"
              value={limitBInput}
              onChange={handleLimitBInputChange}
            /> bytes
          </div>
        </div>
        <div>
          -----====-----
        </div>
      </div>
    </div>
  );
}

function numberToHexString(n, len = 2) {
  return n.toString(16).padStart(len, '0')
}

function CommandForm({memData, refreshMemData}) {
  const [commandText, setCommandText] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [status, setStatus] = useState('typing');
  const [assemble, setAssemble] = useState(false);

  if (status === 'success') {
    console.log("good command :)")
    setStatus('typing')
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('submitting');
    let commandList = commandText.split("\n")
    let commandsArr = commandList.map( (com) => {return com.match(/(\S+)\s?(\".+\"|\S*)\s?(\S*)?/)}  )
    console.log(commandsArr)
    let commandObjList = []

    let doWhatNow = assemble ? 'assemble' : 'run'

    if (!commandsArr.length) {
      setStatus('typing')
      setError({message: "! SYNTAX ERROR !"})
      return true
    }

    for (let command of commandsArr) {
      if (command) {
        let commandObj = {op: command[1]}
        if (command[2].length) {
          commandObj.param = command[2]
        }
        if (command[3] && command[3].length) {
          commandObj.param_b = command[3]
        }
        commandObj.doWhatNow = doWhatNow
        commandObjList.push(commandObj)
      }
    }

    const commandJSON = JSON.stringify({instructions: commandObjList})
    try {
      const commandRes = await submitCommandForm(commandJSON);
      await refreshMemData();
      setStatus('success');
      setError(null);
      console.log('refreshed after command')
      if (commandRes.success !== undefined) {
        setSuccess(commandRes.success.msg)
      }
    } catch (err) {
      setStatus('typing');
      setSuccess(null);
      setError(err);
    }
  }

  function handleTextareaChange(e) {
    setCommandText(e.target.value);
  }

  return (
    <div className="w-max-3xl">
      <div className="text-2xl p-4">user command:</div>
      <form onSubmit={handleSubmit}>
        <textarea
          className="py-1 text-l text-gray-400 bg-gray-800 focus:text-white outline-1 focus:outline-4 focus:outline-dotted outline-solid-2px outline-offset-8"
          value={commandText}
          onChange={handleTextareaChange}
          disabled={status === 'submitting'}
          rows={3}
        />
        <br />
        <button className="btn" disabled={
          commandText.length === 0 ||
          status === 'submitting'
        }>
          SUBMIT
        </button>

        <ControlCheckbox controlValue={assemble} controlLabel="assemble" onControlChange={setAssemble} />

        {error !== null &&
        <p className="text-red-400">
            {':( :( :( ___' + error.message + '___ ): ): ):'}
        </p>
        }
        {success !== null &&
        <p className="text-green-400">
            {':) _' + success + '_ (:'}
        </p>
        }
      </form>
    </div>
  );
}

function submitCommandForm(commandJSON) {
  return new Promise((resolve, reject) => {
    fetch(cpuUrl, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: commandJSON
    }).then(async (res) => {  
                        const the_json = await res.json()
                        if (the_json.errors) {
                          console.log('command result JSON errors:', the_json.errors)
                          reject(new Error(the_json.errors[0].msg))
                        }
                        else {
                          console.log('command result JSON:', the_json)
                          resolve(the_json)
                        }
                  }).catch((err) => console.log('command error is this a thing?', err))
    });
}


